import React from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { getStore } from '../ApplicationState';
import { isElementaryStudent } from '../UserManagementHelpers';
import { ShowSurveyParameters } from '../../peekapak-types/SurveyCampaignTypes';
import {
  recordUserChoice,
  getStudentDestination,
  getPlayUrl,
  getSurveySequenceCount,
  getSurveyBackgroundColor,
} from '../SurveyChoiceHelpers';
import { safeSessionStorage } from '../safeStorage';
import { sendUserToRoleHomePage } from '../GlobalFunctions';
import SpinnerOverlay from '../SpinnerOverlay';
import styles from './SurveyChoice.module.scss';
import Analytics from '../Analytics';

interface LocationState {
  nextPathname?: string;
  surveyParameters?: ShowSurveyParameters;
}

const SurveyChoice: React.FC = () => {
  const location = useLocation<LocationState>();
  const history = useHistory();
  const { userProfile } = getStore().getState().user;

  // Check for stored next survey parameters from a chain navigation
  const storedNextSurvey = React.useMemo(() => {
    try {
      const stored = safeSessionStorage.getItem('nextSurveyParameters');
      if (stored) {
        const params = JSON.parse(stored);
        // Clear it immediately after reading to prevent reuse
        safeSessionStorage.removeItem('nextSurveyParameters');
        return params;
      }
    } catch (error) {
      console.error('Error reading stored survey parameters:', error);
    }
    return null;
  }, []);

  // Check if this is part of a survey chain
  const isChainedSurvey = !!storedNextSurvey;

  // Get the current survey sequence count for color cycling (per-user)
  const surveySequenceNumber = userProfile ? getSurveySequenceCount(userProfile.userId) : 1;
  const detailsBoxBackgroundColor = getSurveyBackgroundColor(surveySequenceNumber);

  // Get survey parameters from stored chain, location state, or user profile
  const surveyParameters =
    storedNextSurvey?.surveyParameters || location.state?.surveyParameters || userProfile?.surveyParameters;
  const nextPathname = storedNextSurvey?.nextPathname || location.state?.nextPathname;

  // Handle missing survey parameters with useEffect to avoid render-phase navigation
  React.useEffect(() => {
    if (!surveyParameters && userProfile) {
      console.log('[SurveyChoice] No survey parameters, redirecting via sendUserToRoleHomePage');
      sendUserToRoleHomePage(userProfile, history, true, false);
    }
  }, [surveyParameters, userProfile, history]);

  console.log('[SurveyChoice] Rendering:', {
    hasSurveyParameters: !!surveyParameters,
    source:
      surveyParameters === storedNextSurvey?.surveyParameters
        ? 'sessionStorage'
        : surveyParameters === location.state?.surveyParameters
          ? 'location.state'
          : 'userProfile',
    isChainedSurvey,
    nextPathname,
    userType: userProfile?.type,
    campaignId: surveyParameters?.campaignId,
    locationState: !!location.state,
    profileSurveyParams: !!userProfile?.surveyParameters,
  });

  if (!surveyParameters) {
    // Show loading while navigation effect runs
    return <SpinnerOverlay isShow={true} />;
  }

  // Generate a more descriptive title based on available survey parameters
  const getSurveyTitle = () => {
    if (!surveyParameters) return 'Survey Available';

    const domain = surveyParameters.surveyDomain;
    const respondentGroup = surveyParameters.respondentGroup;

    // Format the domain name (replace underscores/hyphens with spaces and capitalize)
    const formattedDomain = domain.replace(/[-_]/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

    const part1 = `${formattedDomain} Survey`;
    const part2 = respondentGroup ? ` for ${respondentGroup.replace(',', ', ')}` : '';

    return part1 + part2;
  };

  // Generate a contextual description based on the survey domain
  const getSurveyDescription = () => {
    if (!surveyParameters) {
      return "We have a short survey that would help us understand how you're doing. Your responses are valuable and will highlight areas to grow and celebrate";
    }

    const domain = (surveyParameters.surveyDomain || 'well-being').toLowerCase();

    // Provide context-specific descriptions based on the survey domain
    if (domain.includes('well-being') || domain.includes('wellbeing')) {
      return "This well-being survey helps us understand how you're doing and how we can better support you. Your honest responses help strengthen your school community.";
    } else if (domain.includes('social') || domain.includes('emotional')) {
      return 'This social-emotional learning survey helps us understand your experiences and feelings. Your feedback helps us create better learning environments.';
    } else if (domain.includes('academic')) {
      return 'This academic survey helps us understand your learning experience. Your insights will highlight areas to grow and celebrate.';
    } else if (domain.includes('feedback')) {
      return "We'd love to hear your feedback! This survey helps us understand your experience and how we can make improvements.";
    } else {
      // Generic description for other survey types
      return `This ${domain.replace(/[-_]/g, ' ')} survey helps us better understand your needs and experiences. Your responses help strengthen your school community.`;
    }
  };

  const handleTakeSurvey = () => {
    if (userProfile) {
      recordUserChoice(userProfile.userId, surveyParameters.campaignId, 'take');
      Analytics.internalControlClickEvent('SurveyChoice_TakeSurvey', {
        controlType: 'button',
        metadata: {
          surveyCampaignId: surveyParameters.campaignId,
          surveyDomain: surveyParameters.surveyDomain,
          respondentGroup: surveyParameters.respondentGroup,
        },
        userProfile,
      });
    } else {
      Analytics.internalControlClickEvent('SurveyChoice_TakeSurvey', {
        controlType: 'button',
        metadata: {
          surveyCampaignId: surveyParameters.campaignId,
          surveyDomain: surveyParameters.surveyDomain,
          respondentGroup: surveyParameters.respondentGroup,
        },
      });
    }
    history.push('/survey', {
      nextPathname,
      surveyParameters,
    });
  };

  const handleSkipSurvey = () => {
    if (userProfile) {
      recordUserChoice(userProfile.userId, surveyParameters.campaignId, 'skip');
    }
    Analytics.internalControlClickEvent('SurveyChoice_SkipSurvey', {
      controlType: 'button',
      metadata: {
        surveyCampaignId: surveyParameters.campaignId,
        surveyDomain: surveyParameters.surveyDomain,
        respondentGroup: surveyParameters.respondentGroup,
      },
      userProfile,
    });

    console.log('[SurveyChoice] User skipping survey:', {
      campaignId: surveyParameters.campaignId,
      userType: userProfile?.type,
      currentPath: history.location.pathname,
    });

    // Defer navigation to ensure localStorage write completes
    // and React state updates propagate
    setTimeout(() => {
      // Pass fromLoginOrDirectLink=true to allow navigation away from survey page
      sendUserToRoleHomePage(userProfile, history, true, true);
    }, 10);
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.content}>
          <div className={styles.iconContainer}>
            <svg className={styles.surveyIcon} fill='none' viewBox='0 0 24 24' stroke='currentColor'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01'
              />
            </svg>
          </div>

          {isChainedSurvey && (
            <div
              className={styles.chainIndicator}
              style={{
                marginBottom: '1rem',
                padding: '0.75rem',
                backgroundColor: '#f0f9ff',
              }}
            >
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#1e40af', textAlign: 'center' }}>
                Another survey is available for you
              </p>
            </div>
          )}

          <h1 className={styles.title}>{getSurveyTitle()}</h1>

          <p className={styles.description}>
            {isChainedSurvey
              ? 'We have another survey ready for you. Feel free to continue now or take a break and come back later.'
              : getSurveyDescription()}
          </p>

          <div className={styles.detailsBox} style={{ backgroundColor: detailsBoxBackgroundColor }}>
            <h3 className={styles.detailsTitle}>Survey Details:</h3>
            <ul className={styles.detailsList}>
              {surveyParameters?.surveyDomain && (
                <li className={styles.detailsItem}>
                  Topic: {surveyParameters.surveyDomain.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                </li>
              )}
              <li className={styles.detailsItem}>Takes about 3-5 minutes to complete</li>
              <li className={styles.detailsItem}>Your responses are confidential</li>
              <li className={styles.detailsItem}>Helps highlight areas to grow and celebrate</li>
              <li className={styles.detailsItem}>
                If you&rsquo;re not ready now, you can complete this survey next time
              </li>
            </ul>
          </div>

          <div className={styles.buttonContainer}>
            <button onClick={handleTakeSurvey} className={styles.primaryButton}>
              {isChainedSurvey ? 'Continue with Survey' : 'Take Survey'}
            </button>

            <button onClick={handleSkipSurvey} className={styles.secondaryButton}>
              {isChainedSurvey ? 'Skip Remaining Surveys' : 'Skip for Now'}
            </button>
          </div>

          <p className={styles.footerNote}>
            {isChainedSurvey
              ? "If you skip, you'll continue to Peekapak and can complete these surveys at another time."
              : userProfile.type === 'Teacher' || userProfile.type.includes('Admin')
                ? "Choosing 'Skip for Now' will take you to your teacher dashboard."
                : isElementaryStudent(userProfile)
                  ? "Choosing 'Skip for Now' will take you myPeekaville."
                  : "Choosing 'Skip for Now' will take you to your student portal."}
          </p>
        </div>
      </div>
    </div>
  );
};

export default SurveyChoice;
